import { useAuth0 } from '@auth0/auth0-react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { Auth0User } from '@externalTypes/auth0';

import { Auth0ErrorState } from '@/components/Errors/Auth0ErrorState';
import { InitFailedState } from '@/components/Errors/InitFailedState';
import PageSpinner from '@/components/PageSpinner';
import { useHandleAuthRedirect } from '@/hooks/initialization/useHandleAuthRedirect';
import { useRequestUserData } from '@/hooks/initialization/useRequestUserData';
import { useSetExternalServices } from '@/hooks/initialization/useSetExternalServices';
import { useStoreLoggedUser } from '@/hooks/initialization/useStoreLoggedUser';
import { clearSelectedOrgFromStorage } from '@/pages/OrgSwitcher/utils';
import { loggedOut } from '@/redux/slices/loggedUserSlice';
import { useAppSelector } from '@/redux/store';
import { useAppDispatch } from '@/redux/store';
import ElectrolyteRoutes from '@/router/routes';

import { useRequestUserOrganization } from './hooks/initialization/useRequestUserOrganization';
import { useSetOrgFromStorage } from './hooks/initialization/useSetOrgFromStorage';
import { SessionStorageItems } from './types/sessionStorageItems';
import AccountLinking from './pages/AccountLinking/AccountLinking';
import {
  HandleAccountLinkArgs,
  Auth0CandidateUser,
} from './pages/AccountLinking/auth0-link-user-types';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';
import MaintenancePage from './pages/MaintenancePage';
import { useI18nFeatureFlag } from '@/hooks/useI18nFeatureFlag';
import { useInitializeLanguageFromUserData } from '@/hooks/initialization/useInitializeLanguageFromUserData';
import { usePostEmbeddedSignupComplete } from '@/hooks/initialization/usePostEmbeddedSignupComplete';
import { useShowAlmostThereModal } from '@/hooks/initialization/useShowAlmostThereModal';
import { EmbedBlocker } from './components/EmbedBlocker';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import { EmbeddedFirstLogin } from '@/pages/EmbeddedFirstLogin/EmbeddedFirstLogin';
import { shouldTriggerFirstLoginSignup } from '@/pages/EmbeddedFirstLogin/shouldTriggerFirstLoginSignup';
import { CopilotKitProvider } from '@/components/CopilotKitProvider';
import { OrgSelector } from '@/pages/OrgSelector/OrgSelector';

/**
 * Type for Auth0 user with custom claims used in AuthenticatedApp.
 * accountLinkingInfo is used for account linking. First-login signup detection
 * and its claim parsing live in EmbeddedFirstLogin.
 */
type Auth0UserWithClaims = {
  'https://my.electric.ai/accountLinkingInfo'?: unknown;
  [key: string]: unknown;
};

const AuthenticatedApp = () => {
  const loggedUser = useAppSelector(state => state.loggedUser);
  const selectedOrgIdFromState = loggedUser?.organizationId;
  const selectedOrgRolesFromState = loggedUser?.organizationUserRoles;
  const selectedEmployeeIdFromState = loggedUser?.employeeId;

  const dispatch = useAppDispatch();
  const { showErrorToast, showSuccessToast } = useGlobalUI();
  const { itHubMaintenancePage } = useFlags();
  // TODO(TF-677): replace with gigawatt_setup_assistant entitlement once core-entity ships it
  const { canAccessGigawattForSetupAssistant } = useAIChatFeatureFlags();

  // Track unauthorized errors in embedded context to show error state instead of reloading
  const [showUnauthorizedError, setShowUnauthorizedError] = useState(false);

  // Enforce i18n language settings based on feature flag
  useI18nFeatureFlag();

  // Initialize language from user's saved preference in backend
  useInitializeLanguageFromUserData();

  // auth0 hook (here is the only place in the app that handles auth0 directly)
  const {
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    user: auth0User,
    isLoading: auth0UserLoading,
    isAuthenticated: auth0UserAuthenticated,
    error: auth0Error,
    getIdTokenClaims,
  } = useAuth0();

  const { isEmbedded } = useIsEmbedded();

  /**
   * Detect if user is in the account linking flow.
   *
   * Account linking occurs when a user attempts to log in with an email that already exists
   * in our system but is associated with a different authentication provider. For example:
   * - User originally signed up with username/password
   * - User later attempts to log in with Google using the same email
   *
   * When this happens, Auth0 rules inject the 'accountLinkingInfo' custom claim into the
   * ID token. This claim contains information about both the current user and candidate
   * users that can be linked together.
   *
   * During account linking:
   * - Normal initialization flow is skipped (useStoreLoggedUser, useRequestUserData)
   * - User is shown the AccountLinking UI to choose how to link accounts
   * - After linking, user is redirected to refresh their session with the linked account
   *
   * useMemo is used here to:
   * 1. Ensure stable reference to prevent unnecessary re-renders in dependent hooks
   * 2. Explicitly declare auth0User as a dependency for better code maintainability
   * 3. Provide proper type safety with the Auth0UserWithClaims type assertion
   */
  const isAccountLinking = useMemo(() => {
    if (!auth0User) {
      return false;
    }
    const userWithClaims = auth0User as Auth0UserWithClaims;
    return !!userWithClaims['https://my.electric.ai/accountLinkingInfo'];
  }, [auth0User]);

  /**
   * Detect if user is in the first login (JIT signup) flow.
   *
   * This occurs when a user is logging in through an HRIS SSO connection for the
   * first time and;
   *    a) does not have a user in Electric or,
   *    b) the company does not exist in Electric yet
   *
   * This flow signs up the user automatically, creating a new organization for them
   * if needed (or just a new user), and then logs them in.
   *
   * During this flow we skip the normal initialization. After the user completes the signup process,
   * they are redirected to refresh their session and complete the normal initialization.
   *
   * The Composite Post Login script is the one that determines wether the user
   * needs to sign up or not. That is based on whether the user exists in
   * Electric's DB.
   *
   * It is worth to mention that the signup endpoint is idempotent so it can be
   * called more than one time for the same parameters. The result will not be duplicate
   * organizations.
   *
   * A user enters this flow when they have the signup claim and log in through
   * a known HRIS connection — see shouldTriggerFirstLoginSignup. It does not
   * depend on whether the app is running in an iframe.
   */
  const isFirstLoginSignup = useMemo(
    () => shouldTriggerFirstLoginSignup(auth0User),
    [auth0User]
  );

  const handleEmbeddedSignupSuccess = useMemo(() => {
    return (authorizationParams?: Record<string, string>) => {
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
        // For isolved, these carry connection/tenantId/clientId — required
        // again here since the callback navigation stripped them from the URL.
        ...(authorizationParams &&
          Object.keys(authorizationParams).length > 0 && {
            authorizationParams,
          }),
      });
    };
  }, [loginWithRedirect]);

  const handleSkipAccountLink = useCallback(
    async (connection: string, email: string) => {
      await loginWithRedirect({
        authorizationParams: {
          connection,
          login_hint: email,
        },
      });
    },
    [loginWithRedirect]
  );

  // Handle unauthenticated user (redirect to Auth0's Login page)
  // We probably don't need this anymore since we're not rendering this component for unauthenticated users
  // @TODO: Move conditionals in useHandleAuthRedirect to UnauthenticatedApp.tsx and remove this hook
  useHandleAuthRedirect({
    isInMaintenanceMode:
      itHubMaintenancePage || isAccountLinking || isFirstLoginSignup,
    auth0UserLoading,
    auth0UserAuthenticated,
    loginWithRedirect,
    auth0Error,
    isEmbedded,
  });

  // When user is authenticated, create a loggedUser in the store (with token and some auth0's data)
  useStoreLoggedUser({
    auth0UserAuthenticated:
      auth0UserAuthenticated && !isAccountLinking && !isFirstLoginSignup,
    getAccessTokenSilently,
    auth0User,
    loginWithRedirect,
  });

  // Check for previously selected org before the app is initialized
  useSetOrgFromStorage({
    loggedUser,
    selectedOrgIdFromState,
  });

  // After loggedUser is created (login is done), request the user data.
  // This will skip setting the org info if it was set in the previous dispatch step,
  // Otherwise the app "auto-selects" the first org it finds - unless the user's
  // connection requires an org selection (see ORG_SELECTOR_ENABLED_CONNECTIONS)
  // and they have multiple orgs, in which case orgSelectionRequired is set
  // instead and OrgSelector takes over the decision.
  // The response data is automatically saved by the extraReducers.
  const {
    isRequestingUserData,
    userDataRequestCompleted,
    myUserDataError,
    isUnauthorizedError,
    orgSelectionRequired,
    orgSelectionChoices,
  } = useRequestUserData({
    auth0UserAuthenticated:
      auth0UserAuthenticated && !isAccountLinking && !isFirstLoginSignup,
    auth0User: auth0User as Auth0User | undefined,
    loggedUser,
    selectedOrgId: selectedOrgIdFromState,
  });

  // After user data is ready, request the organization data using the selected org data,
  // and fetch employee data.
  const {
    isRequestingOrgData,
    orgDataRequestCompleted,
    organizationError,
    employeeError,
  } = useRequestUserOrganization({
    selectedOrgId: selectedOrgIdFromState,
    selectedOrgRoles: selectedOrgRolesFromState,
    selectedEmployeeId: selectedEmployeeIdFromState,
    userDataRequestCompleted,
  });

  // Set/reset external services as org/employee changes (but only after extra data is ready)
  const { externalServicesFirstSetCompleted, launchDarklyError } =
    useSetExternalServices({
      loggedUser,
      extraDataRequestCompleted:
        userDataRequestCompleted && orgDataRequestCompleted,
    });

  // For returning embedded users, post SignupCompleteMessage once initialization is done.
  // First-login embedded users receive this message via EmbeddedFirstLogin instead.
  usePostEmbeddedSignupComplete({
    isEmbedded: isEmbedded && !isFirstLoginSignup && !isAccountLinking,
    isReady: externalServicesFirstSetCompleted,
    organizationId: selectedOrgIdFromState,
    auth0User,
  });

  // Show the "Almost there!" modal once per login for isolved-only orgs that
  // only have one employee so far (employee data is still syncing in).
  useShowAlmostThereModal({
    isReady:
      !isAccountLinking &&
      !isFirstLoginSignup &&
      externalServicesFirstSetCompleted,
    organizationId: selectedOrgIdFromState,
  });

  // Handle unauthorized errors by logging out the user
  // This runs when isUnauthorizedError changes, preventing render-time side effects
  // Skip handling if user is in special flows (account linking, embedded first login)
  useEffect(() => {
    // Don't handle unauthorized errors during account linking or embedded first login flows
    if (isAccountLinking || isFirstLoginSignup) {
      return;
    }

    if (isUnauthorizedError) {
      if (isEmbedded) {
        // In embedded iframes, show error state without logging out.
        // Don't call logout() as it unmounts this component (isAuthenticated becomes false).
        // Instead, show error state and let user refresh to recover.
        setShowUnauthorizedError(true);
      } else {
        // Non-embedded: clear both Auth0 and Redux state, then redirect to logout endpoint
        dispatch(loggedOut());
        clearSelectedOrgFromStorage();
        logout({ logoutParams: { returnTo: window.location.origin } });
      }
    } else {
      // Reset error state when unauthorized error clears
      setShowUnauthorizedError(false);
    }
  }, [
    isUnauthorizedError,
    logout,
    dispatch,
    isEmbedded,
    isAccountLinking,
    isFirstLoginSignup,
  ]);

  if (itHubMaintenancePage) {
    return <MaintenancePage />;
  }

  if (auth0Error) {
    return (
      <Auth0ErrorState
        auth0Error={auth0Error}
        logout={logout}
        loggedOut={loggedOut}
      />
    );
  }

  // Show error state for unauthorized embedded users instead of reloading
  if (showUnauthorizedError && isEmbedded) {
    return <InitFailedState />;
  }

  // Function used to handle client-side account linking for auth0.
  // This is used when a user is logging into an account with an e-mail
  // that we already have in our system from another provider.
  // e.g. Someone uses Google Social auth to log in but they already have
  // an auth0 user with that same e-mail.
  // See here for more information:
  // https://auth0.com/docs/manage-users/user-accounts/user-account-linking/user-initiated-account-linking-client-side-implementation
  const handleAccountLink = async ({
    targetUserIDToken,
    targetUserAccessToken,
    makePrimary,
    targetUserId,
  }: HandleAccountLinkArgs) => {
    // Determine how to link based on what account should be the primary account.

    const primaryAccountAccessToken = makePrimary
      ? targetUserAccessToken
      : await getAccessTokenSilently();
    const secondaryAccountIDtoken = makePrimary
      ? (await getIdTokenClaims())?.__raw
      : targetUserIDToken;
    const primaryUserId = makePrimary ? targetUserId : auth0User?.sub;
    // Make a call to the Auth0 Management API to link the accounts.
    // This uses the scopes 'read:current_user update:current_user_identities' which
    // were provided to both the Auth0Provider and the Auth0Client.
    try {
      const accountLinkResponse = await fetch(
        `${window._env_.VITE_AUTH0_DOMAIN}/api/v2/users/${primaryUserId}/identities`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${primaryAccountAccessToken}`,
          },
          body: JSON.stringify({
            link_with: secondaryAccountIDtoken,
          }),
        }
      );
      if (accountLinkResponse.ok) {
        // Show the success toast.
        showSuccessToast({
          id: 'account-link-toast',
          message:
            'Account linked successfully. You will be redirected back to the application.',
        });
        // Wait 3 seconds before redirecting to allow the user to see the toast.
        setTimeout(() => {
          // We call loginWithRedirect to refresh the user's session and get the new token
          // which is now linked to the other account.
          loginWithRedirect({
            appState: {
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          });
        }, 3000);
      } else {
        throw new Error(accountLinkResponse.statusText);
      }
    } catch (err) {
      showErrorToast({
        id: 'account-link-error-toast',
        message:
          'Error linking account. If this error persists please reach out to support@electric.ai.',
      });
      console.error('Error linking account', err);
    }
  };

  // If the user has an account linking info, show the account linking page.
  if (isAccountLinking && auth0User) {
    const userWithClaims = auth0User as Auth0UserWithClaims;
    const accountLinkingInfo = userWithClaims[
      'https://my.electric.ai/accountLinkingInfo'
    ] as Auth0CandidateUser;

    if (accountLinkingInfo) {
      return (
        <AccountLinking
          currentUser={auth0User}
          userToAuth={accountLinkingInfo}
          handleAccountLink={handleAccountLink}
          onSkipAccountLink={handleSkipAccountLink}
        />
      );
    }
  }

  // If embedded first login is detected, EmbeddedFirstLogin handles claim parsing,
  // validation, and signup. It shows InitFailedState when claims are invalid or
  // the connection is unsupported.
  if (isFirstLoginSignup) {
    return (
      <EmbeddedFirstLogin
        auth0User={auth0User}
        getIdTokenClaims={getIdTokenClaims}
        onSignupSuccess={handleEmbeddedSignupSuccess}
      />
    );
  }

  // User's connection requires an org selection and they have multiple orgs:
  // let them choose instead of silently landing in whichever org
  // useRequestUserData would have picked.
  if (orgSelectionRequired) {
    return (
      <OrgSelector
        orgChoices={orgSelectionChoices}
        email={loggedUser?.email}
        onUseDifferentAccount={() => {
          dispatch(loggedOut());
          clearSelectedOrgFromStorage();
          logout({ logoutParams: { returnTo: window.location.origin } });
        }}
      />
    );
  }

  // handle initialization errors
  const initFailed =
    myUserDataError || organizationError || employeeError || launchDarklyError;

  if (initFailed) {
    // Clear sessionStorage to avoid infinite loop in case it's causing the error
    sessionStorage.removeItem(SessionStorageItems.SELECTED_ORG_DATA);

    return <InitFailedState />;
  }

  // Render "loading": While the app is initializing (getting essential data and setting up external services for the first time)
  if (
    auth0UserLoading ||
    isRequestingUserData ||
    !userDataRequestCompleted ||
    isRequestingOrgData ||
    !orgDataRequestCompleted ||
    !externalServicesFirstSetCompleted // Launch Darkly is initialized with user's org and id
  ) {
    return <PageSpinner />;
  }

  // Render the application itself (after authentication and initialization is done)
  return (
    <EmbedBlocker>
      <Suspense fallback={<PageSpinner />}>
        {canAccessGigawattForSetupAssistant ? (
          <CopilotKitProvider>
            <ElectrolyteRoutes />
          </CopilotKitProvider>
        ) : (
          <ElectrolyteRoutes />
        )}
      </Suspense>
    </EmbedBlocker>
  );
};

export default AuthenticatedApp;
